import { isAssignedSku } from '../utils/quotableDesign';
import { setAnalyticsUserId, trackEvent } from './analytics';

export const PORTAL_EVENTS = {
  COMPANY_CREATED: 'company_created',
  USER_CREATED: 'user_created',
  PROJECT_CREATED: 'project_created',
  DESIGN_CREATED: 'design_created',
  QUOTE_REQUESTED: 'quote_requested',
  QUOTE_ACCEPTED: 'quote_accepted',
  INVOICE_PAID: 'invoice_paid',
};

export const PENDING_COMPANY_CREATED_KEY = 'ga_pending_company_created';
export const PENDING_USER_CREATED_KEY = 'ga_pending_user_created';

const firedEventKeys = new Set();
const seenDesignUniqueIds = new Map();
const UTILIZATION_KEYS = ['utilization_pct', 'utilization', 'max_utilization', 'max_uc', 'uc'];

const toId = (value) => {
  if (value == null || value === '') {
    return undefined;
  }
  return String(value);
};

const toMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return undefined;
  }
  return Math.round(amount * 100) / 100;
};

const firstIncludedDesignId = (record) => {
  const raw = record?.included_designs ?? record?.includedDesigns;
  if (Array.isArray(raw) && raw.length > 0) {
    return toId(raw[0]);
  }
  return undefined;
};

const getUtilizationPct = (design) => {
  const pools = [
    design,
    design?.sky_civ_details,
    design?.sky_civ_details?.config,
    design?.sky_civ_details?.results,
    design?.sky_civ_details?.frame_data,
  ];

  for (const pool of pools) {
    if (!pool) continue;
    for (const key of UTILIZATION_KEYS) {
      const n = Number(pool[key]);
      if (!Number.isFinite(n)) continue;
      const pct = n <= 1 ? n * 100 : n;
      return Math.round(pct * 10) / 10;
    }
  }

  return undefined;
};

const fireOnce = (key, eventName, params) => {
  if (key && firedEventKeys.has(key)) {
    return;
  }
  if (key) {
    firedEventKeys.add(key);
  }
  trackEvent(eventName, params);
};

export const getUserRole = (user) => {
  if (!user) {
    return undefined;
  }
  if (user.is_super_admin) {
    return 'super_admin';
  }
  if (user.is_admin) {
    return 'admin';
  }
  if (String(user.user_type || '').toLowerCase() === 'design only') {
    return 'design_only';
  }
  return 'member';
};

export const getMountSeries = (productType) => {
  if (productType === 'eco-beam' || productType === 'ecobeam') {
    return 'ecobeam';
  }
  if (productType === 'standard-beam' || productType === 'standard') {
    return 'standard';
  }
  return productType || undefined;
};

export const isInvoicePaid = (invoice) =>
  String(invoice?.invoice_status || '').toLowerCase() === 'paid';

export const trackCompanyCreated = ({ companyId, userId, method } = {}) => {
  if (userId) {
    setAnalyticsUserId(userId);
  }

  fireOnce(
    companyId ? `${PORTAL_EVENTS.COMPANY_CREATED}:${companyId}` : undefined,
    PORTAL_EVENTS.COMPANY_CREATED,
    {
      company_id: toId(companyId),
      method: method || 'email',
    }
  );
};

export const trackUserCreated = ({ companyId, userId, userRole } = {}) => {
  fireOnce(
    userId ? `${PORTAL_EVENTS.USER_CREATED}:${userId}` : undefined,
    PORTAL_EVENTS.USER_CREATED,
    {
      company_id: toId(companyId),
      user_role: userRole || 'member',
    }
  );
};

export const trackProjectCreated = ({
  companyId,
  projectId,
  projectType,
  mountSeries,
} = {}) => {
  fireOnce(
    projectId ? `${PORTAL_EVENTS.PROJECT_CREATED}:${projectId}` : undefined,
    PORTAL_EVENTS.PROJECT_CREATED,
    {
      company_id: toId(companyId),
      project_id: toId(projectId),
      project_type: projectType ? String(projectType).toLowerCase() : undefined,
      mount_series: getMountSeries(mountSeries),
    }
  );
};

export const trackDesignCreated = ({
  companyId,
  projectId,
  design,
  designId,
  utilizationPct,
} = {}) => {
  const sku = toId(designId || design?.unique_id);
  const numericDesignId = design?.id;
  fireOnce(
    numericDesignId
      ? `${PORTAL_EVENTS.DESIGN_CREATED}:${numericDesignId}`
      : sku
        ? `${PORTAL_EVENTS.DESIGN_CREATED}:${sku}`
        : undefined,
    PORTAL_EVENTS.DESIGN_CREATED,
    {
      company_id: toId(companyId),
      project_id: toId(projectId || design?.project_site_id),
      design_id: sku,
      utilization_pct: utilizationPct ?? getUtilizationPct(design),
    }
  );
};

/**
 * Record a design's current SKU and fire design_created only when a real SKU
 * is assigned. First observation of an already-SKU'd historical design does
 * not fire unless `fireIfAlreadyAssigned` is set (used at create-time).
 */
export const rememberAndTrackDesignCreated = (
  design,
  { companyId, projectId } = {},
  { fireIfAlreadyAssigned = false } = {}
) => {
  if (!design?.id) {
    return;
  }

  const previousUniqueId = seenDesignUniqueIds.get(design.id);
  const nextUniqueId = design.unique_id || null;
  seenDesignUniqueIds.set(design.id, nextUniqueId);

  const assignedNow = isAssignedSku(nextUniqueId);
  const assignedBefore = isAssignedSku(previousUniqueId);
  const isFirstObservation = previousUniqueId === undefined;
  const shouldFire =
    (isFirstObservation && fireIfAlreadyAssigned && assignedNow) ||
    (!isFirstObservation && !assignedBefore && assignedNow);

  if (shouldFire) {
    trackDesignCreated({
      companyId,
      projectId: projectId || design.project_site_id,
      design,
    });
  }
};

export const rememberAndTrackDesignsCreated = (designs, context = {}) => {
  (designs || []).forEach((design) => rememberAndTrackDesignCreated(design, context));
};

export const trackQuoteRequested = ({
  companyId,
  projectId,
  designId,
  quote,
  value,
} = {}) => {
  const quoteId = toId(quote?.quote_number || quote?.id);
  fireOnce(
    quoteId ? `${PORTAL_EVENTS.QUOTE_REQUESTED}:${quoteId}` : undefined,
    PORTAL_EVENTS.QUOTE_REQUESTED,
    {
      company_id: toId(companyId),
      project_id: toId(projectId || quote?.project_site_id),
      design_id: toId(designId) || firstIncludedDesignId(quote),
      quote_id: quoteId,
      value: toMoney(value ?? quote?.total_amount),
      currency: 'USD',
    }
  );
};

export const trackQuoteAccepted = ({
  companyId,
  projectId,
  designId,
  quote,
  value,
} = {}) => {
  const quoteId = toId(quote?.quote_number || quote?.id);
  fireOnce(
    quoteId ? `${PORTAL_EVENTS.QUOTE_ACCEPTED}:${quoteId}` : undefined,
    PORTAL_EVENTS.QUOTE_ACCEPTED,
    {
      company_id: toId(companyId),
      project_id: toId(projectId || quote?.project_site_id),
      design_id: toId(designId) || firstIncludedDesignId(quote),
      quote_id: quoteId,
      value: toMoney(value ?? quote?.total_amount),
      currency: 'USD',
    }
  );
};

export const trackInvoicePaid = ({ companyId, projectId, invoice } = {}) => {
  const invoiceId = toId(invoice?.invoice_number || invoice?.id);
  fireOnce(
    invoiceId ? `${PORTAL_EVENTS.INVOICE_PAID}:${invoiceId}` : undefined,
    PORTAL_EVENTS.INVOICE_PAID,
    {
      company_id: toId(companyId),
      project_id: toId(projectId || invoice?.project_site_id),
      quote_id: toId(invoice?.quote_number || invoice?.quote_id),
      invoice_id: invoiceId,
      value: toMoney(invoice?.total_amount),
      currency: 'USD',
    }
  );
};

/**
 * Fire invoice_paid only when an already-known invoice transitions to Paid.
 * Passing `previousInvoices === null` snapshots the current list without firing.
 */
export const trackInvoicePaidTransitions = (
  previousInvoices,
  nextInvoices,
  { companyId } = {}
) => {
  if (!previousInvoices) {
    return;
  }

  const previousById = new Map((previousInvoices || []).map((invoice) => [invoice.id, invoice]));
  (nextInvoices || []).forEach((invoice) => {
    const previous = previousById.get(invoice.id);
    if (!previous) {
      return;
    }
    if (!isInvoicePaid(previous) && isInvoicePaid(invoice)) {
      trackInvoicePaid({
        companyId,
        projectId: invoice.project_site_id,
        invoice,
      });
    }
  });
};

export const consumePendingCompanyCreated = ({ companyId, userId } = {}) => {
  try {
    const method = sessionStorage.getItem(PENDING_COMPANY_CREATED_KEY);
    if (!method || !companyId) {
      return;
    }
    sessionStorage.removeItem(PENDING_COMPANY_CREATED_KEY);
    trackCompanyCreated({ companyId, userId, method });
  } catch {
    // ignore storage errors
  }
};

export const consumePendingUserCreated = ({ companyId, userId, userRole } = {}) => {
  try {
    const pending = sessionStorage.getItem(PENDING_USER_CREATED_KEY);
    if (!pending || !companyId) {
      return;
    }
    sessionStorage.removeItem(PENDING_USER_CREATED_KEY);
    trackUserCreated({ companyId, userId, userRole });
  } catch {
    // ignore storage errors
  }
};

export const markPendingCompanyCreated = (method = 'email') => {
  try {
    sessionStorage.setItem(PENDING_COMPANY_CREATED_KEY, method);
  } catch {
    // ignore storage errors
  }
};

export const markPendingUserCreated = () => {
  try {
    sessionStorage.setItem(PENDING_USER_CREATED_KEY, '1');
  } catch {
    // ignore storage errors
  }
};
